import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { getCountry } from 'countries-and-timezones';

const AddEditContactModal = ({
  isOpen,
  onClose,
  onSave,
  contact = null,
  isLoading = false,
  error = null
}) => {
  const [formData, setFormData] = useState({
    displayName: '',
    phoneNumber: '',
    country: ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Determine if this is edit mode
  const isEditMode = contact && contact.contactId;

  // Enhanced country detection function
  const detectCountryFromPhone = (phoneNumber) => {
    if (!phoneNumber || phoneNumber.trim() === '') {
      return '';
    }

    const cleanNumber = phoneNumber.trim();
    let parsedNumber = null;

    // Try parsing the number as-is first
    parsedNumber = parsePhoneNumberFromString(cleanNumber);
    
    // If parsing failed and number doesn't start with +, try adding + prefix
    if (!parsedNumber && !cleanNumber.startsWith('+')) {
      parsedNumber = parsePhoneNumberFromString('+' + cleanNumber);
    }

    // If we have a valid parsed number with country, get country name
    if (parsedNumber && parsedNumber.country) {
      const countryData = getCountry(parsedNumber.country);
      return countryData ? countryData.name : '';
    }

    return '';
  };

  // Initialize form data when modal opens or contact changes
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && contact.contactData) {
        const phoneNumber = contact.phone || '';
        const detectedCountry = detectCountryFromPhone(phoneNumber);
        setFormData({
          displayName: contact.contactData.displayName || '',
          phoneNumber: phoneNumber,
          country: detectedCountry
        });
      } else {
        // For add mode, prefill phoneNumber if available, reset displayName
        const phoneNumber = contact?.phone || '';
        const detectedCountry = detectCountryFromPhone(phoneNumber);
        setFormData({
          displayName: '',
          phoneNumber: phoneNumber,
          country: detectedCountry
        });
      }
      setValidationErrors({});
    }
  }, [contact, isEditMode, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-detect country when phone number changes
    if (field === 'phoneNumber') {
      const detectedCountry = detectCountryFromPhone(value);
      setFormData(prev => ({
        ...prev,
        country: detectedCountry
      }));
    }

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.displayName.trim()) {
      errors.displayName = 'Name is required';
    }

    if (!isEditMode && !formData.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const contactData = {
      phone_number: isEditMode ? contact.phone : formData.phoneNumber.trim(),
      display_name: formData.displayName.trim()
    };

    onSave(contactData, isEditMode ? contact.contactId : null);
  };

  const handleClose = () => {
    setFormData({
      displayName: '',
      phoneNumber: '',
      country: ''
    });
    setValidationErrors({});
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="contact-modal__overlay" onClick={handleOverlayClick}>
      <div className="contact-modal__container">
        {/* Close Button */}
        <button
          className="contact-modal__close-button"
          onClick={handleClose}
          disabled={isLoading}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="#737C8A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Content */}
        <div className="contact-modal__content">
          {/* Header */}
          <div className="contact-modal__header">
            <div className="contact-modal__title-section">
              <h2 className="contact-modal__title">
                {isEditMode ? 'Edit Contact' : 'Add Contact'}
              </h2>
            </div>
          </div>

          {/* Form */}
          <div className="contact-modal__form">
            {/* Name Field */}
            <div className="contact-modal__field">
              <label className="contact-modal__label">Name</label>
              <input
                type="text"
                className="contact-modal__input"
                value={formData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                placeholder="John Doe"
                disabled={isLoading}
              />
              {validationErrors.displayName && (
                <div className="contact-modal__error">{validationErrors.displayName}</div>
              )}
            </div>

            {/* Phone and Country Row */}
            <div className="contact-modal__phone-row">
              {/* Phone Field */}
              <div className="contact-modal__phone-field">
                <label className="contact-modal__label">Phone</label>
                <input
                  type="text"
                  className="contact-modal__input"
                  value={isEditMode ? (contact?.phone || '') : formData.phoneNumber}
                  onChange={isEditMode ? undefined : (e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder={isEditMode ? '' : '+60123456789'}
                  disabled={isEditMode || isLoading}
                />
                {validationErrors.phoneNumber && (
                  <div className="contact-modal__error">{validationErrors.phoneNumber}</div>
                )}
              </div>

              {/* Country Field */}
              <div className="contact-modal__country-field">
                <label className="contact-modal__label">Country</label>
                <input
                  type="text"
                  className="contact-modal__input"
                  value={formData.country}
                  disabled
                />
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="contact-modal__error" style={{ textAlign: 'center' }}>
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="contact-modal__actions">
            <button
              className="contact-modal__button contact-modal__button--cancel"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="contact-modal__button contact-modal__button--primary"
              onClick={handleSave}
              disabled={isLoading || !formData.displayName.trim() || (!isEditMode && !formData.phoneNumber.trim())}
            >
              {isLoading ? (
                <div className="contact-modal__loading">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray="31.416"
                      strokeDashoffset="31.416"
                      style={{
                        animation: 'spin 1s linear infinite',
                        transformOrigin: 'center'
                      }}
                    />
                  </svg>
                  Saving...
                </div>
              ) : (
                isEditMode ? 'Save Changes' : 'Add Contact'
              )}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          to {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </div>
  );
};

AddEditContactModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  contact: PropTypes.shape({
    phone: PropTypes.string,
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contactData: PropTypes.shape({
      displayName: PropTypes.string
    })
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.string
};

export default AddEditContactModal;
